import { create } from 'zustand';
import { QueryDocumentSnapshot, DocumentData, Unsubscribe } from '@firebase/firestore';
import { AppNotification } from '../types';
import { notificationsApi } from '../api/notifications';
import { getErrorMessage } from '../utils/errorHandler';
import { withLoading } from './utils/withLoading';

interface NotificationsState {
  notifications: AppNotification[];
  unreadCount: number;
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  isLoading: boolean;
  error: string | null;

  fetchNotifications: (userId: string, refresh?: boolean) => Promise<void>;
  fetchMore: (userId: string) => Promise<void>;
  markAsRead: (notificationId: string, userId: string) => Promise<void>;
  markAllAsRead: (userId: string) => Promise<void>;
  subscribeToUnreadCount: (userId: string) => Unsubscribe;
  /**
   * Web-only: request browser notification permission, then register the FCM
   * token for the given user. Returns the resulting permission state so the
   * caller can update UI accordingly. No-op (and logs a warning) if called
   * outside a browser context. Rethrows FCM errors.
   */
  registerWebPushToken: (userId: string) => Promise<NotificationPermission>;
  clearError: () => void;
  reset: () => void;
}

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  lastDoc: null,
  hasMore: true,
  isLoading: false,
  error: null,

  fetchNotifications: async (userId: string, refresh = false) => {
    if (refresh) {
      set({ lastDoc: null, hasMore: true });
    }
    await withLoading(set, 'isLoading', async () => {
      const { notifications, lastDoc } = await notificationsApi.getNotifications(userId);
      set({
        notifications,
        lastDoc,
        hasMore: notifications.length >= 20,
      });
    });
  },

  fetchMore: async (userId: string) => {
    const { hasMore, isLoading, lastDoc, notifications } = get();
    if (!hasMore || isLoading || !lastDoc) return;

    await withLoading(set, 'isLoading', async () => {
      const result = await notificationsApi.getNotifications(userId, lastDoc);
      set({
        notifications: [...notifications, ...result.notifications],
        lastDoc: result.lastDoc,
        hasMore: result.notifications.length >= 20,
      });
    });
  },

  markAsRead: async (notificationId: string, userId: string) => {
    const { notifications } = get();
    // Already read → nothing to write, and decrementing the unreadCount
    // counter again would make it drift negative.
    const target = notifications.find((n) => n.id === notificationId);
    if (target?.read) return;

    // Optimistic update — mark as read locally immediately
    set({
      notifications: notifications.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
    });

    try {
      await notificationsApi.markAsRead(notificationId, userId);
    } catch (error) {
      // No rollback needed for minor read-state inconsistency
      set({ error: getErrorMessage(error) });
    }
  },

  markAllAsRead: async (userId: string) => {
    // Optimistic update — mark all as read locally
    const { notifications } = get();
    const prevNotifications = notifications;
    const prevUnreadCount = get().unreadCount;
    set({
      notifications: notifications.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    });

    try {
      await notificationsApi.markAllAsRead(userId);
    } catch (error) {
      // Rollback on error
      set({
        notifications: prevNotifications,
        unreadCount: prevUnreadCount,
        error: getErrorMessage(error),
      });
    }
  },

  subscribeToUnreadCount: (userId: string) => {
    return notificationsApi.subscribeToUnreadCount(userId, (count) => {
      set({ unreadCount: count });
    });
  },

  registerWebPushToken: async (userId: string) => {
    return await notificationsApi.registerWebPushToken(userId);
  },

  clearError: () => {
    set({ error: null });
  },

  reset: () => {
    set({
      notifications: [],
      unreadCount: 0,
      lastDoc: null,
      hasMore: true,
      isLoading: false,
      error: null,
    });
  },
}));
